import React, { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";

// css
import styles from "./addForm.module.scss";
import { FieldArray, Formik } from "formik";
import AppInputField from "components/shared/FormComponents/AppInputField";
import AppSelectField from "components/shared/FormComponents/AppSelectField";
import { cloneDeep } from "lodash";
import { useNavigate, useParams, Link } from "react-router-dom";
import { successMessageToast } from "helpers/common";
import { MANAGE_CMS_LIST } from "config/paths";
import LoadingIndicator from "components/shared/LoadingIndicator";
import addContentSchema from "./addContent.schema";
import { MIME_TYPES, STATUS_OPTIONS } from "config/constants";
import { RequiredFieldAstrick } from "components/Common/Common";
import { useCreateCmsMutation, useDeleteCmsResourceMutation, useGetCmsDetailQuery, useUpdateCmsMutation } from "store/services/cms";
import AppFileUploadField from "components/shared/FormComponents/AppFileUploadField";
import { DocumentIcon, MinusIcon, PlusIcon, RemoveIcon } from "Assets/icons";
import ConfirmationPop from "components/Modals/ConfirmationPop";
import LoadingOverlay from "components/shared/LoadingOverlay";
import CKEditorField from "components/CkEditor/CkEditor";

const AddCms = () => {

  const { id: contentId } = useParams();
  const editMode = !!contentId;
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [initialValues, setInitialValues] = useState({
    title: "",
    slug: "",
    status: "active",
    seo_title: "",
    seo_keyword: "",
    seo_desc: "",
    cover_img: "",
    videos: [],
    description: "",
    documents: [{
      title: "",
      description: "",
      attachment: {
        attachment_name: "",
        attachment_key: ""
      }
    }]
  });

  const [updateCms, { isLoading: updateCmsLoading }] = useUpdateCmsMutation();
  const [createCms, { isLoading: createCmsLoading }] = useCreateCmsMutation();
  const [deleteCmsVideo, { isLoading: deleteCmsVideoLoading }] = useDeleteCmsResourceMutation();
  const { data: teacherDetails, isLoading } = contentId ? useGetCmsDetailQuery(contentId) : {};

  const handleSubmit = async (payload) => {

    try {

      const values = cloneDeep(payload);

      const result = editMode ? await updateCms({ data: values, contentId }) : await createCms(values);
      if (result?.error) {

        console.error("Error create cms", result.error);
        return;
      };

      successMessageToast(result?.data?.message);
      navigate(MANAGE_CMS_LIST);

    } catch (err) { console.error("Error create cms", err); }
  };

  const handleUploadVideos = (name, value, prevValues, setFieldValue) => {
    try {
      let cloneValues = cloneDeep(prevValues);
      cloneValues.push({
        attachment_key: value,
        attachment_name: value,
      })
      setFieldValue(name, cloneValues);
    } catch (err) {
      console.error("Failed to attach document", err);
    }
  };

  const handleVideoDelete = async () => {

    try {

      setShowModal(false);
      const result = await deleteCmsVideo(currentEntry.id);
      if (result?.error) {

        console.error("Error delete cms video", result.error);
        return;
      };

      successMessageToast(result?.data?.message);
    } catch (err) { console.error("Error create cms", err); }
  };

  const handleAttachDocument = (name, value, setFieldValue) => {
    try {

      const valueSplit = value.split("/");
      const attachment_name = valueSplit[valueSplit?.length - 1];
      setFieldValue(name, { attachment_key: value, attachment_name });
    } catch (err) {
      console.error("Failed to attach document", err);
    }
  };

  useEffect(() => {

    if (!contentId) return;

    if (teacherDetails) {

      setInitialValues({
        ...initialValues,
        title: teacherDetails?.title || "",
        slug: teacherDetails?.slug || "",
        status: teacherDetails?.status || "",
        seo_title: teacherDetails?.seo_title || "",
        seo_keyword: teacherDetails?.seo_keyword || "",
        seo_desc: teacherDetails?.seo_desc || "",
        cover_img: teacherDetails?.cover_img || "",
        videos: teacherDetails?.videos || "",
        description: teacherDetails?.description || "",
        documents: teacherDetails?.type == "policy_procedures" ? teacherDetails?.documents?.length ? teacherDetails?.documents : [singleDocument] : []
      });
    };

  }, [teacherDetails]);

  return (
    <>
      <ConfirmationPop
        confirmation={showModal}
        setConfirmation={setShowModal}
        confirmHandler={handleVideoDelete}
        type="delete"
      />
      <section className={`${styles.addForm} position-relative py-4 addForm`}>
        <Container fluid={true}>
          <Row>
            <Col lg="12" className="my-2">
              <div className="TableFilter d-flex align-items-center justify-content-between gap-10 flex-wrap">
                <div className="left">
                  <div className="pageHead pb-3 d-flex align-items-center">
                    <Link
                      className="themeBlue fw-sbold back-to-btn"
                      to="/cms/"
                    >
                      <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 12L0 6L6 0L7.425 1.4L2.825 6L7.425 10.6L6 12ZM18 14V10C18 9.16667 17.7083 8.45833 17.125 7.875C16.5417 7.29167 15.8333 7 15 7H8.825L12.425 10.6L11 12L5 6L11 0L12.425 1.4L8.825 5H15C16.3833 5 17.5625 5.4875 18.5375 6.4625C19.5125 7.4375 20 8.61667 20 10V14H18Z" fill="#1C1B1F" />
                      </svg> Back
                    </Link>
                    <h6 className="m-0 fw-bold themeBlue">{editMode ? "Edit Page" : "Add New Page"}</h6>
                  </div>
                </div>
              </div>

            </Col>
            {
              isLoading ? <LoadingIndicator /> :
                <Col lg="12" className="my-2">
                  <Formik
                    initialValues={initialValues}
                    validationSchema={addContentSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                  >
                    {({
                      handleSubmit,
                      values,
                      setFieldValue,
                      errors
                    }) => (
                      <Form onSubmit={handleSubmit}>
                        {
                          deleteCmsVideoLoading ? <LoadingOverlay /> : null
                        }
                        <div
                          className="py-3"
                          style={{ borderTop: "1px solid #41a2a84d" }}
                        >
                          <Row>
                            <Col md="6" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Title
                                <RequiredFieldAstrick />
                              </label>
                              <AppInputField
                                type="text"
                                name="title"
                                placeholder="Enter Title"
                                className={`${styles.formControl} form-control`}
                                maxLength={30}
                                onBlur={() => {
                                  if (!editMode && !values.slug) {

                                    const slug = values.title
                                      .toLowerCase()
                                      .trim()
                                      .replace(/ /g, '-')
                                      .replace(/[^\w-]/g, '')
                                      .replace(/^-|-$/g, '');

                                    setFieldValue("slug", slug)
                                  }
                                }}
                              />
                            </Col>
                            <Col md="6" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Slug
                              </label>
                              <AppInputField
                                type="text"
                                name="slug"
                                placeholder="Enter Slug"
                                className={`${styles.formControl} form-control`}
                                maxLength={30}
                              />
                            </Col>
                            <Col md="12" sm="12" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Description
                                <RequiredFieldAstrick />
                              </label>
                              <div className={`ck-editor-wrapper`} >
  
                                <CKEditorField
                                  handleChange={(event, editor) => setFieldValue("description", editor?.getData())}
                                  value={initialValues?.description}
                                />
                              </div>
                              {errors?.description ? <div className="error">{errors?.description}</div> : null}
                            </Col>
                            {
                              teacherDetails?.type == "policy_procedures" ? <Col md="12" sm="12" className="my-2">
                                <label
                                  htmlFor=""
                                  className="form-label themeBlue fw-bold mb-1 d-flex my-2"
                                  style={{ fontSize: "15px" }}
                                >
                                  Policy Procedures
                                </label>
                                <FieldArray name="documents">
                                  {({ insert, remove, push }) => (
                                    <ul className="list-unstyled ps-0 mb-0">
                                      {values.documents.length > 0 &&
                                        values.documents.map((area, index) => {
                                          let isLastItem = index === values.documents.length - 1;
                                          return (
                                            <Row className="align-items-center" key={index}>
                                              <Col lg="10">
                                                <Row>
                                                  <Col md="8" sm="12" className="my-2">
                                                    <Col md="12" sm="12" className="">
                                                      <label
                                                        htmlFor=""
                                                        className="form-label themeBlue fw-bold mb-0 d-flex"
                                                      >
                                                        Title
                                                        <RequiredFieldAstrick />
                                                      </label>
                                                      <AppInputField
                                                        name={`documents.${index}.title`}
                                                        placeholder="Title"
                                                        className={`${styles.formControl} form-control`}
                                                      />
                                                    </Col>
                                                    <Col md="12" sm="12" className="my-2">
                                                      <label
                                                        htmlFor=""
                                                        className="form-label themeBlue fw-bold mb-0 d-flex"
                                                      >
                                                        Description
                                                        <RequiredFieldAstrick />
                                                      </label>
                                                      <AppInputField
                                                        name={`documents.${index}.description`}
                                                        placeholder="Description"
                                                        fieldType={"textarea"}
                                                        maxLength={500}
                                                        className={`${styles.formControl} form-control`}
                                                      />
                                                    </Col>
                                                  </Col>
                                                  <Col md="4" sm="12" className="my-2">
                                                    <label
                                                      htmlFor=""
                                                      className="form-label themeBlue fw-bold mb-0 d-flex"
                                                    >
                                                      Document
                                                      <RequiredFieldAstrick />
                                                    </label>
                                                    <AppFileUploadField
                                                      name={`documents.${index}.attachment`}
                                                      acl={"public-read"}
                                                      accept="application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                                                      allowedTypes={[MIME_TYPES.pdf, MIME_TYPES.ppt, MIME_TYPES.pptx, MIME_TYPES.pptm]}
                                                      className={`${styles.formControl} form-control`}
                                                      onChange={(name, value) => {
                                                        handleAttachDocument(name, value, setFieldValue);
                                                      }}
                                                    />
                                                    {area?.attachment?.attachment_name ? <div className="left d-flex align-items-center">
                                                      <span className="icn me-2">
                                                        <DocumentIcon />
                                                      </span>
                                                      <p className="m-0 text-muted">{area?.attachment?.attachment_name}</p>
                                                    </div > : null}
                                                  </Col>
                                                </Row>
                                              </Col>
                                              <Col lg="2">
                                                <Button
                                                  className="border-0 p-0"
                                                  variant="transparnet"
                                                >
                                                  {isLastItem ? (

                                                    values.documents.length > 1 ? (
                                                      <>
                                                        <PlusIcon onClick={() => push({ ...singleDocument })} />
                                                        <MinusIcon onClick={() => remove(index)} />
                                                      </>
                                                    ) : <PlusIcon onClick={() => push({ ...singleDocument })} />

                                                  ) : <MinusIcon onClick={() => remove(index)} />
                                                  }
                                                </Button>
                                              </Col>
                                            </Row>
                                          )
                                        })}
                                    </ul>
                                  )}
                                </FieldArray>
                              </Col> : null
                            }
                            <Col md="6" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Status
                                <RequiredFieldAstrick />
                              </label>
                              <AppSelectField
                                name={"status"}
                                placeholder={"Please Select"}
                                className={`${styles.formControl} form-control`}
                                options={STATUS_OPTIONS}
                              />
                            </Col>
                            <Col md="6" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Cover Image
                              </label>
                              <AppFileUploadField
                                name="cover_img"
                                acl={"public-read"}
                                accept="image/png"
                                allowedTypes={[MIME_TYPES.png]}
                                className={`${styles.formControl} form-control`}
                                preview
                              />
                            </Col>
                            <Col md="12" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                SEO Title
                              </label>
                              <AppInputField
                                type="text"
                                name="seo_title"
                                placeholder="Enter SEO Title"
                                className={`${styles.formControl} form-control`}
                                maxLength={60}
                              />
                            </Col>
                            <Col md="12" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                SEO Keyword
                              </label>
                              <AppInputField
                                type="text"
                                name="seo_keyword"
                                placeholder="Enter SEO Keyword"
                                className={`${styles.formControl} form-control`}
                                maxLength={100}
                              />
                            </Col>
                            <Col md="12" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                SEO Description
                              </label>
                              <AppInputField
                                type="text"
                                fieldType={"textarea"}
                                name="seo_desc"
                                placeholder="Enter SEO Description"
                                className={`${styles.formControl} ${styles.textArea} form-control`}
                                maxLength={160}
                                rows="5"
                              />
                            </Col>
                          </Row>
                        </div>
                        <div
                          className="py-3"
                          style={{ borderTop: "1px solid #41a2a84d" }}
                        >
                          <Row className="mt-1 ">
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Videos
                              </label>
                              <AppFileUploadField
                                name="videos"
                                acl={"public-read"}
                                accept="video/mp4"
                                allowedTypes={[MIME_TYPES.video]}
                                className={`${styles.formControl} form-control`}
                                onChange={(name, value) => {
                                  handleUploadVideos(name, value, values?.videos, setFieldValue);
                                }}
                                hidePreview
                              />
                            </Col>
                          </Row>
                          <Row className="mt-1 ">
                            {
                              values?.videos?.map((item, index) => {
                                return <Col md="4" sm="6" className="my-2">
                                  <div key={index} className="w-100 d-flex align-items-center" style={{ position: "relative" }}>
                                    <video className="w-100" controls>
                                      <source src={item?.attachment_key} type={MIME_TYPES.video} />
                                    </video>

                                    <Button
                                      className="border-0 p-0"
                                      style={{ position: "absolute", right: "10px", top: "5px" }}
                                      variant={"transparent"}
                                      onClick={() => {

                                        if (item.id) {

                                          setCurrentEntry(item);
                                          setShowModal(true);
                                        } else {

                                          const videos = cloneDeep(values?.videos)

                                          delete videos[index];
                                          setFieldValue("videos", videos.filter(Boolean))
                                        }

                                      }}
                                    >
                                      <RemoveIcon style={{ height: "20px", width: "20px" }} />
                                    </Button>
                                  </div>
                                </Col>
                              })
                            }
                          </Row>
                        </div>
                        <Row className="mt-4">
                          <Col className="my-2" lg="12">
                            <div
                              className={`${styles.btnWrpper} btnWrpper d-flex align-items-center gap-10 pt-3`}
                              style={{ borderTop: "1px solid #41a2a84d" }}
                            >
                              <Button disabled={createCmsLoading || updateCmsLoading} type="submit" className="d-flex align-items-center justify-content-center commonBtn">
                                {createCmsLoading || updateCmsLoading ? <LoadingIndicator inline /> : "Save"}

                              </Button>
                              <Button
                                type="button"
                                className="d-flex align-items-center justify-content-center commonBtn borderedBtn bg-transparent"
                                onClick={() => navigate(MANAGE_CMS_LIST)}
                              >
                                Cancel
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    )}
                  </Formik>
                </Col>
            }
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AddCms;

const singleDocument = {
  title: "",
  description: "",
  attachment: {
    attachment_name: "",
    attachment_key: ""
  }
}